import {
  CheckCircle,
  Cancel,
  MoreVert,
  Add,
  Receipt,
  Inventory,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  Snackbar,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import {
  SERVICE_PACKAGE_TYPE_LABELS,
  SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION,
  SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY,
} from "../../constants";
import ServicePackageEditorDialog from "./dialogs/ServicePackageEditorDialog";

interface ServicePackage {
  id: string;
  name: string;
  description?: string;
  shortDescription?: string;
  packageType: string;
  price: number;
  currency: string;
  displayOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  iconName?: string;
  featuresIncluded?: string[];
  billingType?: string;
  billingInterval?: string;
}

interface ServicePackageOrder {
  id: string;
  uid: string;
  servicePackageId: string;
  pricePaid: number;
  status: string;
  created_date: string;
  package?: ServicePackage;
  userEmail?: string;
}

export default function WhitelabelServicePackages({
  whitelabelId,
  currentUser,
  getToken,
}: {
  whitelabelId: string;
  currentUser: User;
  getToken: () => Promise<string>;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [orders, setOrders] = useState<ServicePackageOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<ServicePackage | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<ServicePackage | null>(
    null
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  useEffect(() => {
    loadPackages();
  }, [whitelabelId]);

  useEffect(() => {
    if (activeTab === 1) {
      loadOrders();
    }
  }, [activeTab, whitelabelId]);

  const loadPackages = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/service-packages?include_inactive=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPackages(response.data.packages || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load service packages");
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/service-packages/orders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setOrders(response.data.items || []);
    } catch (err: any) {
      setSnackbarMessage(
        err.response?.data?.error || "Failed to load orders"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleEditPackage = (pkg: ServicePackage) => {
    setSelectedPackage(pkg);
    setEditDialogOpen(true);
    handleCloseMenu(pkg.id);
  };

  const handleDeletePackage = (pkg: ServicePackage) => {
    setPackageToDelete(pkg);
    setDeleteDialogOpen(true);
    handleCloseMenu(pkg.id);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    packageId: string
  ) => {
    setMenuAnchorEl({ ...menuAnchorEl, [packageId]: event.currentTarget });
  };

  const handleCloseMenu = (packageId: string) => {
    setMenuAnchorEl({ ...menuAnchorEl, [packageId]: null });
  };

  const confirmDelete = async () => {
    if (!packageToDelete) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/service-packages/${packageToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSnackbarMessage("Service package deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setPackageToDelete(null);
      loadPackages();
    } catch (err: any) {
      setSnackbarMessage(
        err.response?.data?.error || "Failed to delete service package"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "primary";
      case "pending":
        return "warning";
      case "fulfilled":
        return "info";
      case "refunded":
        return "error";
      default:
        return "default";
    }
  };

  const handleCreatePackage = () => {
    setSelectedPackage(null);
    setEditDialogOpen(true);
  };

  const renderPackagesTab = () => {
    if (loading && packages.length === 0) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography>Loading service packages...</Typography>
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">Service Packages</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreatePackage}
          >
            Create New Package
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {packages.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              No Service Packages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create service packages to offer one-time professional services to
              your customers during signup.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreatePackage}
            >
              Create Your First Package
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {packages
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map((pkg) => (
                <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      opacity: pkg.isActive ? 1 : 0.6,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6">{pkg.name}</Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Chip
                              label={
                                SERVICE_PACKAGE_TYPE_LABELS[pkg.packageType] ||
                                pkg.packageType
                              }
                              size="small"
                            />
                            {pkg.isFeatured && (
                              <Chip
                                label="Featured"
                                size="small"
                                color="primary"
                              />
                            )}
                          </Box>
                        </Box>
                        <Box>
                          {pkg.isActive ? (
                            <CheckCircle color="primary" />
                          ) : (
                            <Cancel color="disabled" />
                          )}
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, minHeight: 40 }}
                      >
                        {pkg.shortDescription || pkg.description || "No description"}
                      </Typography>

                      <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
                        {formatPrice(pkg.price)}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {" "}
                          {pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION
                            ? pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
                              ? "/month"
                              : "/year"
                            : "one-time"}
                        </Typography>
                      </Typography>

                      {pkg.featuresIncluded && pkg.featuresIncluded.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            Includes:
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {pkg.featuresIncluded.slice(0, 3).map((feature, index) => (
                              <Chip
                                key={index}
                                label={feature}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                            {pkg.featuresIncluded.length > 3 && (
                              <Chip
                                label={`+${pkg.featuresIncluded.length - 3} more`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>

                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, pkg.id)}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchorEl[pkg.id]}
                        open={Boolean(menuAnchorEl[pkg.id])}
                        onClose={() => handleCloseMenu(pkg.id)}
                      >
                        <MenuItem onClick={() => handleEditPackage(pkg)}>
                          Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleDeletePackage(pkg)}>
                          Delete
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Card>
                </Grid>
              ))}
          </Grid>
        )}
      </>
    );
  };

  const renderOrdersTab = () => {
    if (ordersLoading) {
      return (
        <Box>
          <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
        </Box>
      );
    }

    if (orders.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Receipt sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Orders Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When customers purchase service packages, their orders will appear here.
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Package</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(order.created_date)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {order.package?.name || "Unknown Package"}
                  </Typography>
                  {order.package?.packageType && (
                    <Typography variant="caption" color="text.secondary">
                      {SERVICE_PACKAGE_TYPE_LABELS[order.package.packageType] ||
                        order.package.packageType}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {order.userEmail || order.uid}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {formatPrice(order.pricePaid)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    size="small"
                    color={getStatusColor(order.status) as any}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Beta Feature:</strong> Service packages are currently in beta.
        Please test thoroughly before using with customers.
      </Alert>

      <Alert severity="info" sx={{ mb: 3 }}>
        Service packages are one-time fees for professional services like
        onboarding, setup, or training. These are charged separately from
        subscription fees when customers sign up. You keep 100% of all service
        package payments.
      </Alert>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab icon={<Inventory />} iconPosition="start" label="Packages" />
        <Tab icon={<Receipt />} iconPosition="start" label="Orders" />
      </Tabs>

      {activeTab === 0 && renderPackagesTab()}
      {activeTab === 1 && renderOrdersTab()}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Service Package</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the "{packageToDelete?.name}" service
            package? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Package Dialog */}
      <ServicePackageEditorDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedPackage(null);
        }}
        package={selectedPackage}
        whitelabelId={whitelabelId}
        getToken={getToken}
        onSave={() => {
          setSnackbarMessage(
            selectedPackage
              ? "Service package updated successfully!"
              : "Service package created successfully!"
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          loadPackages();
        }}
      />
    </Box>
  );
}
